import React from 'react';
import { ClipboardCheck } from 'lucide-react';
import api from '../../utils/axios';
import CTable from '../shared/c-table';
import Pagination from '../Pagination';
import StatusBadge from '../StatusBadge';
import { useAuth } from '../../contexts/AuthContext';
import { formatDateTime } from '../../utils/dateUtils';
import { amendmentStatus } from './ApprovalFilters';
import {
  QueueSection,
  RowActions,
  actionButtonClass,
  dayColumn,
  employeeColumn,
  useApprovalQueue,
} from './approvalQueue';
import { KIND_LABEL, snapshot } from './daySections';

const DESCRIPTION = 'Filed from the timesheet app. They apply immediately; acknowledging records that you '
  + 'have reviewed them.';

/**
 * Manager/HR queue of timesheet amendments. Acknowledging only records that
 * someone with authority has seen the change; the server refuses your own.
 */
const AmendmentsQueue = ({ filters }) => {
  const { hasPermission } = useAuth();
  const canAcknowledge = hasPermission('timesheet', 'acknowledge');

  const queue = useApprovalQueue({
    endpoint: '/api/timesheet/amendments',
    failureMessage: 'Failed to load amendments',
    filters,
    buildParams: ({ status, departmentId, debouncedSearch }) => ({
      status: amendmentStatus(status),
      department_id: departmentId || undefined,
      q: debouncedSearch || undefined,
    }),
  });

  const acknowledge = () => queue.save(
    () => api.put(`/api/timesheet/amendments/${queue.confirm.id}/acknowledge`),
    'Amendment acknowledged'
  );

  const columns = [
    employeeColumn,
    dayColumn,
    {
      key: 'kind',
      header: 'Request',
      cellClassName: 'whitespace-normal',
      render: (amendment) => (
        <>
          {KIND_LABEL[amendment.kind] || amendment.kind}
          <span className="block text-xs text-gray-500">“{amendment.reason}”</span>
        </>
      ),
    },
    {
      key: 'change',
      header: 'Before → After',
      cellClassName: 'text-xs text-gray-600',
      render: (amendment) => (
        <>
          <span className="block">{snapshot(amendment.before)}</span>
          <span className="block text-gray-900">{snapshot(amendment.after)}</span>
        </>
      ),
    },
    {
      key: 'submitted',
      header: 'Submitted',
      cellClassName: 'text-gray-500',
      render: (amendment) => formatDateTime(amendment.created_at),
    },
    {
      key: 'status',
      header: 'Status',
      render: (amendment) => (
        <StatusBadge status={amendment.status} type="amendment" data-testid={`amendment-${amendment.id}-status`} />
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      cellClassName: 'whitespace-normal',
      render: (amendment) => (
        <RowActions
          queue={queue}
          prefix="amendment"
          row={amendment}
          prompt="Acknowledge this amendment?"
          onConfirm={acknowledge}
        >
          {canAcknowledge && amendment.status === 'pending' && (
            <button
              type="button"
              onClick={() => queue.setConfirm({ id: amendment.id })}
              data-testid={`amendment-${amendment.id}-ack`}
              className={`${actionButtonClass} border-success-600 text-success-700 hover:bg-success-50`}
            >
              Acknowledge
            </button>
          )}
        </RowActions>
      ),
    },
  ];

  return (
    <QueueSection
      queue={queue}
      testId="amendments"
      title="Amendments"
      count={queue.total}
      description={DESCRIPTION}
    >
      <CTable
        columns={columns}
        data={queue.rows}
        loading={queue.loading}
        rowKey="id"
        emptyMessage={
          <div className="text-gray-400">
            <ClipboardCheck className="mx-auto h-12 w-12 mb-3" />
            <p className="text-sm">
              {filters.status === 'pending'
                ? 'No amendments waiting to be acknowledged'
                : 'No amendments found'}
            </p>
          </div>
        }
        getRowProps={(amendment) => ({ 'data-testid': `amendment-row-${amendment.id}` })}
      />
      <Pagination page={queue.page} totalPages={queue.totalPages} onPageChange={queue.setPage} />
    </QueueSection>
  );
};

export default AmendmentsQueue;
